import React from "react";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { billingService } from "../../services/billingService";

interface PlanOverviewProps {
  planType: string;
  billingType: string;
  subscriptionStatus?: string;
  currentPeriodEnd?: string;
}

export const PlanOverview: React.FC<PlanOverviewProps> = ({
  planType,
  billingType,
  subscriptionStatus,
  currentPeriodEnd,
}) => {
  const planName = billingService.getPlanDisplayName(planType);

  const getPlanColorClasses = () => {
    switch (planType) {
      case "free":
        return "bg-gray-100 text-gray-800";
      case "starter":
        return "bg-blue-100 text-blue-800";
      case "professional":
        return "bg-purple-100 text-purple-800";
      case "enterprise":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusBadge = () => {
    if (!subscriptionStatus) return null;

    const statusColors: Record<string, string> = {
      active: "bg-green-100 text-green-800",
      trialing: "bg-blue-100 text-blue-800",
      past_due: "bg-red-100 text-red-800",
      canceled: "bg-gray-100 text-gray-800",
    };

    const statusLabels: Record<string, string> = {
      active: "Active",
      trialing: "Trial",
      past_due: "Past Due",
      canceled: "Canceled",
    };

    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          statusColors[subscriptionStatus] || "bg-gray-100 text-gray-800"
        }`}
      >
        {statusLabels[subscriptionStatus] || subscriptionStatus}
      </span>
    );
  };

  const formatBillingPeriod = () => {
    if (!currentPeriodEnd) return null;
    const endDate = new Date(currentPeriodEnd);
    return endDate.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Current Plan</h3>
          <div className="mt-2 flex items-center space-x-3">
            <span
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getPlanColorClasses()}`}
            >
              {planName} Plan
            </span>
            {getStatusBadge()}
            {billingType === "subscription" && (
              <span className="text-sm text-gray-500">
                {billingType === "subscription" ? "Subscription" : "One-time"}
              </span>
            )}
          </div>
        </div>
        {planType !== "free" && (
          <CheckCircleIcon className="h-5 w-5 text-green-500" />
        )}
      </div>

      {currentPeriodEnd && subscriptionStatus === "active" && (
        <div className="mt-4 text-sm text-gray-600">
          <p>Your billing period ends on {formatBillingPeriod()}</p>
        </div>
      )}

      {planType === "free" && (
        <div className="mt-4 bg-gray-50 rounded-md p-4">
          <p className="text-sm text-gray-600">
            You&apos;re on the free plan. Upgrade to unlock advanced features
            and remove limits.
          </p>
        </div>
      )}
    </div>
  );
};
